import { addDays, fromLocalDateTime, isoWeekday, startOfNextDay, toLocalDate } from "./time";
import type { EpochSeconds, LocalDate } from "./time";

/**
 * Where the daily rest must lie next to a block of rest days: split around it, wholly before or wholly after it (the
 * default of the ArbZG profiles), or wholly before it. Later values are stricter. Mirrors `AdjoiningRestPlacement` of
 * the compliance engine.
 */
export type AdjoiningRestPlacement = "Split" | "OneSide" | "BeforeOnly";

/**
 * Limits of the connection of the daily rest with the Sunday and holiday rest and the substitute rest day (§ 11 (4)
 * ArbZG, CJEU C-477/21). Mirrors `AdjoiningRestLimits` of the compliance engine (shared cases
 * `shared-test-cases/adjoining-rest.json`).
 */
export interface AdjoiningRestLimits {
  /** Daily rest a block of consecutive rest days needs next to it, in seconds. */
  readonly adjoiningRestSeconds: number;
  /** Weekly rest each rest day stands for at least, in seconds. */
  readonly weeklyRestSeconds: number;
  /** Where the daily rest must lie next to the block. */
  readonly placement: AdjoiningRestPlacement;
}

/** Limits of `DE-ArbZG-current`: eleven hours wholly before or after the rest days, 24 hours per rest day. */
export const arbZgAdjoiningRestLimits: AdjoiningRestLimits = {
  adjoiningRestSeconds: 39_600,
  weeklyRestSeconds: 86_400,
  placement: "OneSide",
};

/** A closed working day on the time line. */
export interface RestWorkSpan {
  /** Start of the first work of the day. */
  readonly start: EpochSeconds;
  /** End of the last work of the day. */
  readonly end: EpochSeconds;
}

/**
 * State of a block of rest days: the daily rest lies next to it as demanded (or no work lies before it), work ended or
 * began the rest too early, or the rest after it is still running and not yet long enough.
 */
export type AdjoiningRestStatus = "Connected" | "NotConnected" | "Pending";

/** A block of consecutive rest days with the rest directly before and after it. */
export interface AdjoiningRestBlock {
  /** The rest days of the block, ordered and consecutive. */
  readonly restDays: readonly LocalDate[];
  /** The daily rest the block needs next to it, plus the time a day shorter than its weekly rest lacks, in seconds. */
  readonly requiredSeconds: number;
  /** The rest before the block no earlier block uses, or `null` without work before it. */
  readonly beforeSeconds: number | null;
  /** The rest after the block up to the next block, the next work or the reference. */
  readonly afterSeconds: number;
  /** The state. */
  readonly status: AdjoiningRestStatus;
}

/** Consecutive free rest days of one rest period with their local bounds. */
interface RestDayGroup {
  /** Index of the first span after the rest period. */
  readonly next: number;
  readonly days: {
    readonly date: LocalDate;
    readonly start: EpochSeconds;
    readonly end: EpochSeconds;
  }[];
}

/**
 * Groups the free rest days into blocks and assesses each, like `AdjoiningRest.Assess` of the compliance engine. Rest
 * days are local days from 0:00 to 24:00; a day with work on it is left out. A block needs the daily rest next to it
 * where the placement demands it; a day shorter than the weekly rest (the 23-hour day of the spring clock change) needs
 * the missing time in addition, a longer one keeps its whole length. Blocks of the same rest period never use the same
 * time twice.
 *
 * LEGAL-REVIEW (LR-09, ADR-0185): the placement of the daily rest (the profiles demand it wholly before or after the
 * block; wholly before would be stricter, split around it milder), at least the calendar day and 24 hours per rest day,
 * and one daily rest for a block of consecutive rest days.
 *
 * @param spans - Closed working days, in any order.
 * @param restDays - The rest days (free Sundays and holidays, substitute rest days).
 * @param timeZone - IANA time zone of the place of work.
 * @param limits - The limits.
 * @param reference - Instant up to which a running rest is measured.
 * @returns The blocks in time order.
 */
export function assessRestBlocks(
  spans: readonly RestWorkSpan[],
  restDays: readonly LocalDate[],
  timeZone: string,
  limits: AdjoiningRestLimits,
  reference: EpochSeconds,
): AdjoiningRestBlock[] {
  const ordered = [...spans].sort((left, right) => left.start - right.start);
  const groups = groupRestDays(ordered, restDays, timeZone);
  const blocks: AdjoiningRestBlock[] = [];
  let usedUntil: EpochSeconds | null = null;

  for (const [index, group] of groups.entries()) {
    const afterAnotherBlock = index > 0 && groups[index - 1]?.next === group.next;
    const following = groups[index + 1]?.next === group.next ? groups[index + 1] : undefined;
    const assessed = assessBlock(
      ordered,
      group,
      following,
      afterAnotherBlock ? usedUntil : null,
      limits,
      reference,
    );

    blocks.push(assessed.block);
    usedUntil = assessed.usedUntil;
  }

  return blocks;
}

/**
 * Returns the earliest start after the last work that keeps the daily rest next to the Sundays and public holidays that
 * have passed without work since then (the warning before an early clock-in, spec 7.3.3), like
 * `AdjoiningRest.EarliestStart` of the compliance engine, or `null` if none has passed or the rest before them is
 * enough. A day still running does not count, because work on it would make it a working day.
 *
 * @param lastEnd - End of the last closed working day; no work since then.
 * @param now - The current instant.
 * @param holidays - Public holidays at the place of work, if known.
 * @param timeZone - IANA time zone of the place of work.
 * @param limits - The limits.
 * @returns The earliest start, or `null`.
 */
export function earliestStartAfterRest(
  lastEnd: EpochSeconds,
  now: EpochSeconds,
  holidays: ReadonlySet<LocalDate>,
  timeZone: string,
  limits: AdjoiningRestLimits,
): EpochSeconds | null {
  const passed: LocalDate[] = [];
  const last = toLocalDate(now, timeZone);

  for (let date = toLocalDate(lastEnd, timeZone); date <= last; date = addDays(date, 1)) {
    // Only Sundays and holidays need their local bounds, which are the costly part (the clock renders every second).
    const isProtected = isoWeekday(date) === 7 || holidays.has(date);

    if (
      isProtected &&
      fromLocalDateTime(date, "00:00", timeZone) >= lastEnd &&
      startOfNextDay(date, timeZone) <= now
    ) {
      passed.push(date);
    }
  }

  // The last work as a closed span; it starts a second earlier, so a rest day from its end on is free.
  const blocks = assessRestBlocks(
    [{ start: lastEnd - 1, end: lastEnd }],
    passed,
    timeZone,
    limits,
    now,
  );
  const block = blocks[blocks.length - 1];

  if (block === undefined) {
    return null;
  }

  const before = block.beforeSeconds ?? 0;
  let missingAfter: number;

  if (limits.placement === "Split") {
    missingAfter = block.requiredSeconds - Math.min(before, block.requiredSeconds);
  } else {
    missingAfter = before >= block.requiredSeconds ? 0 : block.requiredSeconds;
  }

  const lastDay = block.restDays[block.restDays.length - 1] ?? last;
  const earliest = missingAfter === 0 ? null : startOfNextDay(lastDay, timeZone) + missingAfter;

  return earliest;
}

function groupRestDays(
  spans: readonly RestWorkSpan[],
  restDays: readonly LocalDate[],
  timeZone: string,
): RestDayGroup[] {
  const groups: RestDayGroup[] = [];
  let next = 0;

  // Rest days and spans are both in time order, so one pass over each finds the span after every day.
  for (const date of [...new Set(restDays)].sort()) {
    const start = fromLocalDateTime(date, "00:00", timeZone);
    const end = startOfNextDay(date, timeZone);

    while (next < spans.length && (spans[next]?.start ?? end) < start) {
      next += 1;
    }

    const endsBefore = next === 0 || (spans[next - 1]?.end ?? start) <= start;
    const startsAfter = next >= spans.length || (spans[next]?.start ?? end) >= end;

    if (!endsBefore || !startsAfter) {
      continue;
    }

    const current = groups[groups.length - 1];
    const lastDay = current?.days[current.days.length - 1];

    if (current?.next === next && lastDay !== undefined && addDays(lastDay.date, 1) === date) {
      current.days.push({ date, start, end });
    } else {
      groups.push({ next, days: [{ date, start, end }] });
    }
  }

  return groups;
}

function assessBlock(
  spans: readonly RestWorkSpan[],
  group: RestDayGroup,
  following: RestDayGroup | undefined,
  usedUntil: EpochSeconds | null,
  limits: AdjoiningRestLimits,
  reference: EpochSeconds,
): { readonly block: AdjoiningRestBlock; readonly usedUntil: EpochSeconds } {
  const previous = spans[group.next - 1];
  const next = spans[group.next];
  const start = group.days[0]?.start ?? reference;
  const end = group.days[group.days.length - 1]?.end ?? reference;
  const shortfall = group.days.reduce(
    (sum, day) => sum + Math.max(0, limits.weeklyRestSeconds - (day.end - day.start)),
    0,
  );
  const required = limits.adjoiningRestSeconds + shortfall;
  const before =
    usedUntil !== null ? start - usedUntil : previous === undefined ? null : start - previous.end;
  const afterLimit = following?.days[0]?.start ?? next?.start ?? reference;
  const afterKnown = following !== undefined || next !== undefined;
  const after = Math.max(0, afterLimit - end);
  const available = before ?? Number.POSITIVE_INFINITY;
  let usedAfter: number;

  if (available >= required) {
    usedAfter = 0;
  } else if (limits.placement === "BeforeOnly") {
    usedAfter = -1;
  } else if (limits.placement === "OneSide") {
    usedAfter = after >= required ? required : -1;
  } else {
    usedAfter = after >= required - available ? required - available : -1;
  }

  let status: AdjoiningRestStatus;

  if (usedAfter >= 0) {
    status = "Connected";
  } else if (limits.placement === "BeforeOnly" || afterKnown) {
    status = "NotConnected";
  } else {
    status = "Pending";
  }

  const block: AdjoiningRestBlock = {
    restDays: group.days.map((day) => day.date),
    requiredSeconds: required,
    beforeSeconds: before,
    afterSeconds: after,
    status,
  };
  const assessed = { block, usedUntil: end + Math.max(0, usedAfter) };

  return assessed;
}
